import {Component, OnInit} from '@angular/core';
import {DatePipe, DecimalPipe, NgClass} from '@angular/common';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {switchMap} from 'rxjs';
import {SubmissionHttpService} from '../../forms/data-access/submission-http.service';
import {FormHttpService} from '../../forms/data-access/form-http.service';
import {isFileRef, Submission, SubmissionStatus} from '../../forms/shared/models/submission.model';
import {
  buildFieldLabelMap, buildFieldMetaMap, FieldMeta, formatFieldValue, FormattedFieldValue, groupSubmissionFields,
  hasReadableValue, isDataImage, LIST_VALUE_CLASSES, OrderedSubmissionField, orderedSubmissionFields,
  submissionFieldId, SubmissionSection, submissionStatusClass, submissionStatusLabel
} from '../../forms/shared/services/submission-display';
import {
  buildFieldNoteMap, FieldNoteMap, FieldNotes, notesForField
} from '../../forms/shared/services/static-text-notes';
import {FieldNoteComponent} from '../../forms/submissions/field-note/field-note.component';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';

/**
 * Employee self-view detail: one of the employee's own submissions with **masked** sensitive values (the default
 * non-privileged read). Field labels come from the form definition. No reveal/download — employees lack the
 * SensitiveDataAccessor role, so those actions are intentionally absent.
 */
@Component({
  selector: 'app-employee-submission-detail',
  standalone: true,
  imports: [DatePipe, DecimalPipe, NgClass, RouterLink, FieldNoteComponent],
  template: `
    <div class="absolute inset-0 overflow-y-auto p-3 sm:p-4 lg:p-6">
      <div class="mx-auto w-full max-w-3xl flex flex-col gap-4">

        <a [routerLink]="['..']"
           class="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
          Meine Formulare
        </a>

        @if (loading) {
          <div class="flex items-center justify-center py-20">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24"
                 xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else if (error || !submission) {
          <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm">
            <p class="text-sm font-semibold text-red-600">Diese Einreichung konnte nicht geladen werden.</p>
            <button (click)="load()"
                    class="mt-2 inline-flex items-center text-red-600 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">
              Erneut versuchen
            </button>
          </div>
        } @else {
          <div class="flex items-center justify-between gap-3">
            <h1 class="text-xl font-bold tracking-tight text-gray-900 truncate">{{ title }}</h1>
            <span class="shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                  [ngClass]="statusClass(submission.status)">{{ statusLabel(submission.status) }}</span>
          </div>

          @if (submission.needsReview) {
            <div class="p-4 bg-yellow-50 border border-yellow-100 rounded-2xl">
              <p class="text-sm font-bold text-yellow-800">Diese Einreichung muss überprüft werden.</p>
              @if (submission.reviewReason) {
                <p class="mt-1 text-sm text-yellow-700">{{ submission.reviewReason }}</p>
              }
              @if (submission.reviewDeadline) {
                <p class="mt-1 text-xs font-semibold text-yellow-700">Fällig am {{ submission.reviewDeadline | date: 'dd.MM.yyyy' }}</p>
              }
            </div>
          }

          <!-- Meta -->
          <dl class="grid grid-cols-2 gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div>
              <dt class="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Eingereicht</dt>
              <dd class="text-sm font-semibold text-gray-900">{{ submission.submittedAt ? (submission.submittedAt | date: 'dd.MM.yyyy HH:mm') : '–' }}</dd>
            </div>
            @if (submission.retainUntil) {
              <div>
                <dt class="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Aufbewahrung bis</dt>
                <dd class="text-sm font-semibold text-gray-900">{{ submission.retainUntil | date: 'dd.MM.yyyy' }}</dd>
              </div>
            }
          </dl>

          <!--
            Field values (masked) — ordered like the form definition, so this matches the edit form. Fields the
            submission holds no value for are filtered out (#495), matching the management view: since #470 a
            hidden field is not stored at all, so a dash row almost always meant "this question did not apply".

            Repeating rows are grouped into their own sections with the list's label and a row number (#499),
            so three side jobs read as three entries rather than "Arbeitgeber" three times over.
          -->
          @for (section of sections; track section.id) {
            <div class="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100">
              @if (section.title) {
                <h2 class="px-4 py-2 text-[10px] font-black text-gray-600 uppercase tracking-widest bg-gray-50/70">
                  {{ section.title }}
                </h2>
              }
              @for (f of section.fields; track fieldId(f)) {
                @let n = notes(f);
                <!--
                  A field row is: [ notes the author put BEFORE it ] · [ label | value ] · [ notes AFTER it ].

                  The notes are siblings of the label/value line, not children of the value column, so an
                  explanation spans the full row width and reads left-aligned like the prose it is. Nested in
                  the right-hand column it squeezed the label into two lines and cramped itself into whatever
                  width was left. Placement comes from the definition, see {@link buildFieldNoteMap} — the same
                  rule the PDF renderer applies, so screen and printout cannot disagree.
                -->
                <div class="px-4 py-3" [attr.data-field]="f.fieldKey">
                  @for (note of n.before; track note.block.id) {
                    <forms-field-note class="mb-1.5 block" data-note-placement="before" [note]="note.block"/>
                  }

                  <div class="flex items-start justify-between gap-4">
                    <!--
                      The label column: capped at 60 % of the row, wrapping between words, deliberately NOT
                      'min-w-0'. All three were measured, not guessed:

                      - WITHOUT the cap, a long consent label ("Mein Geburtsname weicht vom heutigen
                        Familiennamen ab und ich habe die Namensänderung belegt") takes 640 px of a 766 px row
                        at desktop and 277 px of 356 px at 390 px. Flex shrinks the two columns in proportion to
                        their content, so the answer beside it — "Ja" — was crushed to 6.9 px and rendered as
                        "J" over "a". The cap leaves the answer its 16 px and costs the label one extra line.
                      - WITH 'min-w-0' the column may shrink below its min-content width and 'break-words' then
                        breaks mid-word: "Erklärung" came out as "ER / KL / ÄR / UNG" down a 30 px gutter.
                      - Giving the VALUE column 'min-width: auto' instead fixes the same row but makes an
                        unbreakable token (a reference number) push the card to 465 px inside a 356 px viewport.

                      So: the label yields first and has a ceiling, the value stays elastic ('min-w-0' below).
                    -->
                    <span class="block max-w-[60%] break-words text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                      {{ labelFor(f) }}@if (f.isSensitive) {<span class="ml-1 text-theme-primary-500">●</span>}
                    </span>
                    <!-- The value column: right-aligned, and now carrying nothing but the value. -->
                    <div class="min-w-0 text-right">
                      @if (isFile(f.value)) {
                        <span class="text-sm font-medium text-gray-900 break-words">
                          {{ f.value.fileName }} <span class="text-gray-400">({{ f.value.sizeBytes | number }} B)</span>
                        </span>
                      } @else if (isSignature(f)) {
                        <!--
                          A drawn signature is an image, not a value: printing it dumped the whole base64 blob.

                          'block' makes the existing 'ml-auto' the thing that right-aligns it. As an inline image
                          'ml-auto' was inert and the alignment came from the column's inherited 'text-right' —
                          which meant the signature would have silently jumped left the day that changed.

                          'mt-1' is gone (it survives in the management view, where the label sits ABOVE the
                          image and the gap is wanted): here the label is the SIBLING column, so the 4 px pushed
                          the signature's top edge out of line with the label — every other row starts flush.
                        -->
                        <img [src]="f.value" [alt]="'Unterschrift ' + labelFor(f)"
                             class="ml-auto block h-16 w-auto max-w-full rounded-md border border-gray-100 bg-white object-contain p-1"/>
                      } @else {
                        <!--
                          One shared formatter decides how an answer reads (#501): Ja/Nein instead of true/false,
                          the option's label instead of its wire value, and a multi-select as a real list instead
                          of the bracketed, quoted array a JSON.stringify used to print.
                        -->
                        @let v = formatted(f);
                        @if (v.kind === 'list') {
                          <!--
                            The list BLOCK is what this right-aligned column aligns; its CONTENTS are left-aligned
                            so the markers form one column. See {@link LIST_VALUE_CLASSES} for why each class is
                            there — the same constant drives the management view, so the two cannot drift.
                          -->
                          <ul [class]="listClasses"
                              [class.text-gray-900]="!f.isMasked" [class.text-gray-500]="f.isMasked">
                            @for (item of v.items; track $index) {
                              <li>{{ item }}</li>
                            }
                          </ul>
                        } @else {
                          <span class="text-sm font-semibold break-words"
                                [class.text-gray-900]="!f.isMasked" [class.text-gray-500]="f.isMasked"
                                [class.font-mono]="f.isMasked">{{ v.text }}</span>
                        }
                      }
                    </div>
                  </div>

                  @for (note of n.after; track note.block.id) {
                    <forms-field-note class="mt-1.5 block" data-note-placement="after" [note]="note.block"/>
                  }
                </div>
              }
            </div>
          }

          <!-- Revise: edit + resubmit a new version (unchanged masked values are kept). -->
          <div class="flex items-center justify-end gap-3">
            @if (submission.supersededBySubmissionId) {
              <span class="text-xs font-semibold text-gray-400">Durch eine neuere Version ersetzt</span>
            } @else if (canRevise) {
              <button type="button" (click)="revise()" [disabled]="revising"
                      class="inline-flex items-center justify-center px-5 py-2.5 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-semibold shadow-lg shadow-theme-primary-100 hover:shadow-xl transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed">
                Überarbeiten & neu einreichen
              </button>
            }
          </div>
        }
      </div>
    </div>
  `
})
export class EmployeeSubmissionDetailComponent implements OnInit {
  protected loading = true;
  protected error = false;
  protected revising = false;
  protected submission: Submission | null = null;
  /** The submission's field values flattened + ordered like the definition (see {@link orderedSubmissionFields}). */
  protected fields: OrderedSubmissionField[] = [];
  /** Those fields grouped for display: the non-repeating answers, then one section per row (#499). */
  protected sections: SubmissionSection[] = [];
  protected readonly fieldId = submissionFieldId;
  protected title = '';
  private labels: Record<string, string> = {};
  /** `fieldKey → {type, options}`, so a value renders as its option label / `Ja` / a date rather than raw (#501). */
  private meta: Record<string, FieldMeta> = {};
  /** `fieldKey → the notes explaining it, split into before/after` (#501). Empty when no definition loaded. */
  private noteMap: FieldNoteMap = {};

  protected readonly statusLabel = submissionStatusLabel;
  protected readonly statusClass = submissionStatusClass;
  protected readonly isFile = isFileRef;
  /** The `<ul>` classes a list answer renders with — shared with the management view so they cannot drift (#501). */
  protected readonly listClasses = LIST_VALUE_CLASSES;

  constructor(
    private route: ActivatedRoute,
    private http: SubmissionHttpService,
    private forms: FormHttpService,
    private alert: GlobalAlertService,
    private router: Router
  ) {
  }

  /** A finalised, not-yet-superseded submission can be revised (resubmitted as a new version). */
  protected get canRevise(): boolean {
    return !!this.submission
      && this.submission.status !== SubmissionStatus.Draft
      && !this.submission.supersededBySubmissionId;
  }

  /** Start a revision: clone to a draft (server-side), then open the editor on it. */
  protected revise(): void {
    if (!this.submission || this.revising) return;
    this.revising = true;
    this.http.revise(this.submission.id).subscribe({
      next: draft => this.router.navigate(['/ep/submissions', draft.id, 'edit']),
      error: () => {
        this.revising = false;
        this.alert.createAndShow('Fehler', 'Die Überarbeitung konnte nicht gestartet werden.', AlertLevel.error, 3000);
      }
    });
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error = true;
      this.loading = false;
      return;
    }
    this.loading = true;
    this.error = false;

    this.http.get(id).pipe(
      switchMap(submission => {
        this.submission = submission;
        // Load the definition for human-readable field labels + title (best-effort).
        return this.forms.getDefinition(submission.formId);
      })
    ).subscribe({
      next: definition => {
        this.labels = buildFieldLabelMap(definition);
        this.meta = buildFieldMetaMap(definition);
        this.noteMap = buildFieldNoteMap(definition);
        this.fields = this.submission
          ? orderedSubmissionFields(definition, this.submission).filter(f => hasReadableValue(f.value))
          : [];
        this.sections = groupSubmissionFields(definition, this.fields);
        this.title = definition?.title ?? 'Formulareingabe';
        this.loading = false;
      },
      error: () => {
        // The submission may have loaded even if the definition fetch failed — still show values by key (in
        // storage order, since we have no definition to order by).
        this.fields = this.submission
          ? orderedSubmissionFields(null, this.submission).filter(f => hasReadableValue(f.value))
          : [];
        this.sections = groupSubmissionFields(null, this.fields);
        this.error = !this.submission;
        this.title = this.submission ? 'Formulareingabe' : '';
        this.loading = false;
      }
    });
  }

  /** Whether this value is a drawn signature and should render as an image rather than a base64 string. */
  protected isSignature(f: OrderedSubmissionField): boolean {
    return isDataImage(f.value);
  }

  protected labelFor(f: OrderedSubmissionField): string {
    return this.labels[f.fieldKey] ?? f.fieldKey;
  }

  /**
   * How this answer reads, via the one shared formatter (#501).
   *
   * This used to be a local `scalar()` doing `String(value)` / `JSON.stringify(value)` — a near-identical copy
   * of the one in the management detail view — which is exactly why a boolean printed `true`, a select printed
   * `pauschal_2` and a multi-select printed `["keines"]` in both places at once.
   */
  protected formatted(f: OrderedSubmissionField): FormattedFieldValue {
    return formatFieldValue(f.value, this.meta[f.fieldKey]);
  }

  /**
   * The static text the author attached to this field, split into the notes written *before* it and those
   * written *after* it, each in definition order (both empty when nothing explains the field).
   *
   * The split is the whole point: a note the author put above a question used to render below its answer, which
   * is how an instruction ("Tragen Sie hier … ein") ended up reading as a footnote to the value.
   */
  protected notes(f: OrderedSubmissionField): FieldNotes {
    return notesForField(this.noteMap, f.fieldKey);
  }
}
